import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type BodySeries, type BodyDayPoint } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";

const FAT = "#b4531a";
const LEAN = "#5e7a73";

type MetricKey = "body_fat_pct" | "muscle_mass_kg" | "water_pct" | "bone_mass_kg";
const METRICS: { key: MetricKey; label: string; unit: string; color: string }[] = [
  { key: "body_fat_pct", label: "Body fat", unit: "%", color: FAT },
  { key: "muscle_mass_kg", label: "Muscle", unit: "kg", color: LEAN },
  { key: "water_pct", label: "Water", unit: "%", color: "#3f6f8f" },
  { key: "bone_mass_kg", label: "Bone", unit: "kg", color: "#9a8a7a" },
];

export default function WeightComposition() {
  const [data, setData] = useState<BodySeries | null | "error">(null);
  const [metric, setMetric] = useState<MetricKey>("body_fat_pct");

  useEffect(() => {
    // Whole history: composition readings are sparse, and a 90-day window hid
    // every one of them once the scale stopped syncing.
    api.body.series(3650).then(setData).catch(() => setData("error"));
  }, []);

  const d = data && data !== "error" ? data : null;
  const last = d?.last ?? null;
  const has = d != null && d.points.some((p) => p.body_fat_pct != null || p.muscle_mass_kg != null);

  // Current fat vs lean split (kg).
  const split = useMemo(() => {
    if (!last || last.body_fat_pct == null) return null;
    const fat = (last.weight_kg * last.body_fat_pct) / 100;
    const lean = last.weight_kg - fat;
    return { fat: Math.round(fat * 10) / 10, lean: Math.round(lean * 10) / 10, fatPct: last.body_fat_pct };
  }, [last]);

  const active = METRICS.find((m) => m.key === metric)!;
  const sinceStart = changeOf(d?.first ?? null, d?.last ?? null, metric);

  return (
    <div className="animate-fade-in">
      <h2 className="section-title mb-1">Body composition</h2>
      <p className="text-ink-subtle text-small mb-4">
        What the weight is actually made of — so you can tell fat loss from muscle loss, which the
        scale number alone hides.
      </p>

      {!has ? (
        <EmptyState error={data === "error"} title="No body-composition data yet">
          Your Withings scale sends body fat, muscle, water and bone. A weigh-in or two and these
          fill in.
        </EmptyState>
      ) : (
        <>
          {/* Fat vs lean split */}
          {split && (
            <div className="card card-pad">
              <div className="mb-2 flex items-baseline justify-between text-sm">
                <span className="field-label">Fat vs lean mass</span>
                <span className="text-ink-subtle tabular-nums">{split.fatPct}% fat</span>
              </div>
              <div className="flex h-6 w-full overflow-hidden rounded-md">
                <div className="flex items-center justify-center text-[11px] text-white" style={{ width: `${split.fatPct}%`, backgroundColor: FAT }}>
                  {split.fat}kg
                </div>
                <div className="flex items-center justify-center text-[11px] text-white" style={{ width: `${100 - split.fatPct}%`, backgroundColor: LEAN }}>
                  {split.lean}kg lean
                </div>
              </div>
            </div>
          )}

          {/* Metric trend with toggle */}
          <div className="mt-4">
            <div className="mb-3 flex flex-wrap gap-1.5">
              {METRICS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMetric(m.key)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition duration-fast ${
                    metric === m.key ? "bg-accent/10 text-accent" : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <div className="card card-pad">
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-sm text-ink">
                  {active.label}{" "}
                  <span className="text-ink-subtle">
                    {last && last[metric] != null ? `· ${last[metric]}${active.unit}` : ""}
                  </span>
                </span>
                {sinceStart != null && (
                  <span className="text-xs text-ink-subtle tabular-nums">
                    {sinceStart > 0 ? "+" : ""}{sinceStart}{active.unit} since start
                  </span>
                )}
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={d!.points}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} minTickGap={28} />
                  <YAxis axisLine={false} tickLine={false} width={36} fontSize={11} domain={["auto", "auto"]} />
                  <Tooltip formatter={(v: number) => [`${v}${active.unit}`, active.label]} />
                  <Line type="monotone" dataKey={metric} stroke={active.color} strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function changeOf(first: BodyDayPoint | null, last: BodyDayPoint | null, key: MetricKey): number | null {
  if (!first || !last) return null;
  const a = first[key];
  const b = last[key];
  if (a == null || b == null) return null;
  return Math.round((b - a) * 10) / 10;
}
